import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Settings, Database, Bell, Eye } from 'lucide-react';
import { toast } from 'sonner';

interface WorkflowSettings {
  autoSave: boolean;
  autoSaveInterval: number;
  showGridlines: boolean;
  enableAnimations: boolean;
  defaultZoom: number;
  maxLogsRetention: number;
  enableNotifications: boolean;
  theme: 'light' | 'dark' | 'system';
}

const DEFAULT_SETTINGS: WorkflowSettings = {
  autoSave: true,
  autoSaveInterval: 30,
  showGridlines: true,
  enableAnimations: true,
  defaultZoom: 100,
  maxLogsRetention: 1000,
  enableNotifications: true,
  theme: 'system',
};

export function WorkflowSettings() {
  const [settings, setSettings] = useState<WorkflowSettings>(() => {
    const stored = localStorage.getItem('workflow-settings');
    return stored ? { ...DEFAULT_SETTINGS, ...JSON.parse(stored) } : DEFAULT_SETTINGS;
  });

  const [showResetDialog, setShowResetDialog] = useState(false);

  const handleSettingChange = (key: keyof WorkflowSettings, value: any) => {
    const updated = { ...settings, [key]: value };
    setSettings(updated);
    localStorage.setItem('workflow-settings', JSON.stringify(updated));
    toast.success('Setting updated');
  };

  const handleResetSettings = () => {
    setSettings(DEFAULT_SETTINGS);
    localStorage.setItem('workflow-settings', JSON.stringify(DEFAULT_SETTINGS));
    setShowResetDialog(false);
    toast.success('Settings reset to defaults');
  };

  const handleExportSettings = () => {
    const dataStr = JSON.stringify(settings, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `workflow-settings-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Settings exported');
  };

  const handleClearAllData = () => {
    if (confirm('This will delete ALL workflows, logs, secrets, and versions. This cannot be undone.')) {
      localStorage.clear();
      toast.success('All data cleared');
      window.location.reload();
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-foreground mb-2">Workflow Settings</h2>
        <p className="text-muted-foreground">Customize your workflow automation experience</p>
      </div>

      <Tabs defaultValue="general" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="display">Display</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="data">Data</TabsTrigger>
        </TabsList>

        {/* General Settings */}
        <TabsContent value="general" className="space-y-4">
          <Card className="p-6 space-y-6">
            <h3 className="text-lg font-semibold text-foreground">General Settings</h3>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded border border-border">
                <div className="space-y-1">
                  <Label className="text-base font-medium">Auto-save Workflows</Label>
                  <p className="text-sm text-muted-foreground">
                    Automatically save changes to workflows
                  </p>
                </div>
                <Switch
                  checked={settings.autoSave}
                  onCheckedChange={(checked) =>
                    handleSettingChange('autoSave', checked)
                  }
                />
              </div>

              {settings.autoSave && (
                <div className="flex items-center justify-between p-4 rounded border border-border">
                  <div className="space-y-1">
                    <Label className="text-base font-medium">Auto-save Interval (seconds)</Label>
                    <p className="text-sm text-muted-foreground">
                      How often to save changes automatically
                    </p>
                  </div>
                  <Select
                    value={settings.autoSaveInterval.toString()}
                    onValueChange={(value) =>
                      handleSettingChange('autoSaveInterval', parseInt(value))
                    }
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15">15 seconds</SelectItem>
                      <SelectItem value="30">30 seconds</SelectItem>
                      <SelectItem value="60">1 minute</SelectItem>
                      <SelectItem value="300">5 minutes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex items-center justify-between p-4 rounded border border-border">
                <div className="space-y-1">
                  <Label className="text-base font-medium">Default Zoom Level</Label>
                  <p className="text-sm text-muted-foreground">
                    Initial canvas zoom when opening workflows
                  </p>
                </div>
                <Select
                  value={settings.defaultZoom.toString()}
                  onValueChange={(value) =>
                    handleSettingChange('defaultZoom', parseInt(value))
                  }
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="50">50%</SelectItem>
                    <SelectItem value="75">75%</SelectItem>
                    <SelectItem value="100">100%</SelectItem>
                    <SelectItem value="125">125%</SelectItem>
                    <SelectItem value="150">150%</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Display Settings */}
        <TabsContent value="display" className="space-y-4">
          <Card className="p-6 space-y-6">
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Display Settings
            </h3>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded border border-border">
                <div className="space-y-1">
                  <Label className="text-base font-medium">Show Gridlines</Label>
                  <p className="text-sm text-muted-foreground">
                    Display grid background on canvas
                  </p>
                </div>
                <Switch
                  checked={settings.showGridlines}
                  onCheckedChange={(checked) =>
                    handleSettingChange('showGridlines', checked)
                  }
                />
              </div>

              <div className="flex items-center justify-between p-4 rounded border border-border">
                <div className="space-y-1">
                  <Label className="text-base font-medium">Enable Animations</Label>
                  <p className="text-sm text-muted-foreground">
                    Smooth transitions and animations
                  </p>
                </div>
                <Switch
                  checked={settings.enableAnimations}
                  onCheckedChange={(checked) =>
                    handleSettingChange('enableAnimations', checked)
                  }
                />
              </div>

              <div className="flex items-center justify-between p-4 rounded border border-border">
                <div className="space-y-1">
                  <Label className="text-base font-medium">Color Theme</Label>
                  <p className="text-sm text-muted-foreground">
                    Choose your preferred color scheme
                  </p>
                </div>
                <Select
                  value={settings.theme}
                  onValueChange={(value: any) =>
                    handleSettingChange('theme', value)
                  }
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="light">Light</SelectItem>
                    <SelectItem value="dark">Dark</SelectItem>
                    <SelectItem value="system">System</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Notification Settings */}
        <TabsContent value="notifications" className="space-y-4">
          <Card className="p-6 space-y-6">
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Notification Settings
            </h3>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded border border-border">
                <div className="space-y-1">
                  <Label className="text-base font-medium">Enable Notifications</Label>
                  <p className="text-sm text-muted-foreground">
                    Show notifications for important events
                  </p>
                </div>
                <Switch
                  checked={settings.enableNotifications}
                  onCheckedChange={(checked) =>
                    handleSettingChange('enableNotifications', checked)
                  }
                />
              </div>

              <Card className="p-4 bg-blue-500/5 border-blue-500/20">
                <p className="text-sm text-muted-foreground">
                  Notifications will be shown for workflow execution completion,
                  errors, and other important events.
                </p>
              </Card>
            </div>
          </Card>
        </TabsContent>

        {/* Data Settings */}
        <TabsContent value="data" className="space-y-4">
          <Card className="p-6 space-y-6">
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Database className="w-5 h-5" />
              Data Settings
            </h3>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded border border-border">
                <div className="space-y-1">
                  <Label className="text-base font-medium">Logs Retention</Label>
                  <p className="text-sm text-muted-foreground">
                    Maximum number of logs to keep in memory
                  </p>
                </div>
                <Select
                  value={settings.maxLogsRetention.toString()}
                  onValueChange={(value) =>
                    handleSettingChange('maxLogsRetention', parseInt(value))
                  }
                >
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="100">100 logs</SelectItem>
                    <SelectItem value="500">500 logs</SelectItem>
                    <SelectItem value="1000">1000 logs</SelectItem>
                    <SelectItem value="5000">5000 logs</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3 pt-4 border-t border-border">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleExportSettings}
                >
                  Export Settings
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setShowResetDialog(true)}
                >
                  Reset to Defaults
                </Button>
                <Button
                  variant="destructive"
                  className="w-full"
                  onClick={handleClearAllData}
                >
                  Clear All Data
                </Button>
              </div>
            </div>
          </Card>

          <Card className="p-4 bg-amber-500/5 border-amber-500/20">
            <h4 className="font-semibold text-foreground text-sm mb-2">Data Storage</h4>
            <p className="text-sm text-muted-foreground">
              All workflows, logs, secrets, and versions are stored in your browser's
              local storage. Clearing browsing data will remove everything.
            </p>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Reset Dialog */}
      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Settings to Defaults</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to reset all settings to their default values?
              This will not affect your workflows or data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex gap-3 justify-end">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleResetSettings}>
              Reset Settings
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
